import { useCallback } from "react";
import { useNotifications } from "~/contexts/useNotifications";
import { performRoll } from "~/utils/rollUtils";
import { generateUUID } from "~/utils/uuid";
import { computeHpMax, computeResilienceMax } from "~/utils/characterDerived";
import { REST_RULES } from "~/rules/rest";
import type { RestNotification, RollResult } from "~/types/character";

/**
 * Hook for rest actions (snack break, meal break, full rest)
 *
 * Encapsulates the rest mechanics and automatically adds notifications.
 * CharacterSheet-specific (rest is a player action, not GM action).
 *
 * @param coreStats Character's core stats
 * @param currentResilience Current resilience value
 * @param currentHp Current HP value
 * @param strain Current strain value
 * @param updateCurrentResilience Function to update resilience
 * @param updateCurrentHp Function to update HP
 * @param updateStrain Function to update strain
 * @returns Rest action functions
 */
export function useRestActions(
  coreStats: Record<string, number>,
  currentResilience: number,
  currentHp: number,
  strain: number,
  updateCurrentResilience: (value: number) => void,
  updateCurrentHp: (value: number) => void,
  updateStrain: (value: number) => void
) {
  const { addRestNotification, addRollNotification } = useNotifications();

  const takeSnackBreak = useCallback((): void => {
    const resMax = computeResilienceMax(coreStats);
    const resGained = Math.min(REST_RULES.snackBreak.resilienceRecovery, resMax - currentResilience);
    const strainRemoved = Math.min(REST_RULES.snackBreak.strainRemoval, strain);

    updateCurrentResilience(Math.min(currentResilience + REST_RULES.snackBreak.resilienceRecovery, resMax));
    updateStrain(Math.max(0, strain - REST_RULES.snackBreak.strainRemoval));

    // Show notification
    const restNotification: RestNotification = {
      id: generateUUID(),
      restType: "snack",
      resilienceGained: resGained,
      strainRemoved: strainRemoved,
    };
    addRestNotification(restNotification);
  }, [coreStats, currentResilience, strain, updateCurrentResilience, updateStrain, addRestNotification]);

  const takeMealBreak = useCallback((): void => {
    // Recover all Resilience and remove all Strain
    const resMax = computeResilienceMax(coreStats);
    const resGained = resMax - currentResilience;
    const strainRemoved = strain;

    updateCurrentResilience(resMax);
    updateStrain(0);

    // Show notification
    const restNotification: RestNotification = {
      id: generateUUID(),
      restType: "meal",
      resilienceGained: resGained,
      strainRemoved: strainRemoved,
    };
    addRestNotification(restNotification);
  }, [coreStats, currentResilience, strain, updateCurrentResilience, updateStrain, addRestNotification]);

  const takeFullRest = useCallback((): void => {
    // Recover all Resilience and remove all Strain
    const resMax = computeResilienceMax(coreStats);
    const resGained = resMax - currentResilience;
    const strainRemoved = strain;

    updateCurrentResilience(resMax);
    updateStrain(0);

    // Roll Resilience Check (resilience pool) to recover HP
    const pool = resMax;
    const rollResult = performRoll(pool, 0);
    const healAmount = rollResult.hits;

    // Apply healing
    const hpMax = computeHpMax(coreStats);
    const newHp = Math.min(currentHp + healAmount, hpMax);
    updateCurrentHp(newHp);

    // Show rest notification
    const restNotification: RestNotification = {
      id: generateUUID(),
      restType: "fullRest",
      resilienceGained: resGained,
      strainRemoved: strainRemoved,
    };

    // Show roll notification for HP healing
    const newRoll: RollResult = {
      id: generateUUID(),
      name: "Rest (Resilience Check)",
      hits: rollResult.hits,
      rolls: rollResult.rolls,
      isFumble: rollResult.isFumble,
      isCritFumble: rollResult.isCritFumble,
    };

    addRollNotification(newRoll);
    addRestNotification(restNotification);
  }, [coreStats, currentResilience, strain, currentHp, updateCurrentResilience, updateStrain, updateCurrentHp, addRestNotification, addRollNotification]);

  return {
    takeSnackBreak,
    takeMealBreak,
    takeFullRest,
  };
}
